import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@clerk/react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ImageUploadIcon,
  MusicNote01Icon,
  Video01Icon,
  FileAttachmentIcon,
  PencilEdit01Icon,
  Delete01Icon,
} from "@hugeicons/core-free-icons";
import { createAssetsApi } from "@/lib/api/assets";
import { cn } from "@/lib/utils";
import type { Asset } from "@/lib/types/assets";

interface AssetCardProps {
  asset: Asset;
  onRename?: (asset: Asset) => void;
  onDelete?: (asset: Asset) => void;
  onSelect?: (asset: Asset) => void;
  isSelected?: boolean;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const TYPE_ICONS = {
  image: ImageUploadIcon,
  audio: MusicNote01Icon,
  video: Video01Icon,
  document: FileAttachmentIcon,
};

export function AssetCard({ asset, onRename, onDelete, onSelect, isSelected }: AssetCardProps) {
  const { getToken } = useAuth();
  const assetsApi = useMemo(() => createAssetsApi({ getToken }), [getToken]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (asset.assetType !== "image") return;
    let objectUrl: string | null = null;
    assetsApi
      .fetchContent(asset.id)
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      })
      .catch(() => setPreviewUrl(null));
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [asset.id, asset.assetType, assetsApi]);

  const TypeIcon = TYPE_ICONS[asset.assetType] ?? FileAttachmentIcon;
  const isPickerMode = !!onSelect;

  return (
    <div
      onClick={() => onSelect?.(asset)}
      className={cn(
        "group relative flex flex-col rounded-xl border bg-muted/50 overflow-hidden transition-all",
        isPickerMode
          ? "cursor-pointer hover:border-brand/60 hover:bg-muted/80"
          : "hover:border-border/80",
        isSelected && "border-brand/60",
      )}
    >
      {/* Thumbnail / icon area */}
      <div className="relative aspect-square bg-black/[0.03] dark:bg-white/[0.04] flex items-center justify-center overflow-hidden">
        {asset.assetType === "image" && previewUrl ? (
          <img
            src={previewUrl}
            alt={asset.name}
            className="w-full h-full object-cover pointer-events-none"
          />
        ) : (
          <HugeiconsIcon
            icon={TypeIcon}
            size={32}
            className="text-muted-foreground/30"
          />
        )}

        {/* Hover action overlay — only in non-picker mode */}
        {!isPickerMode && (onRename || onDelete) && (
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            {onRename && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRename(asset);
                }}
                className="flex size-7 cursor-pointer items-center justify-center rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors"
                title="Rename"
              >
                <HugeiconsIcon icon={PencilEdit01Icon} size={13} />
              </button>
            )}
            {onDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(asset);
                }}
                className="flex size-7 cursor-pointer items-center justify-center rounded-lg bg-white/20 hover:bg-red-500/70 text-white transition-colors"
                title="Delete"
              >
                <HugeiconsIcon icon={Delete01Icon} size={13} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-2.5 py-2 flex flex-col gap-0.5">
        <span className="text-xs font-medium truncate leading-tight" title={asset.name}>
          {asset.name}
        </span>
        <span className="text-[10px] text-muted-foreground/60">
          {formatFileSize(asset.sizeBytes)}
        </span>
      </div>
    </div>
  );
}
